import React from "react";
import { DOC_CENTER } from "@/constants/testIds";
import { Ship, ClipboardCheck, ReceiptText, Package, Anchor, FileSignature, Truck, ArrowDown } from "lucide-react";

const NODES = [
  { slug: "shipment", label: "Shipment", code: "SH-2481", icon: Ship },
  { slug: "purchase-order", label: "Purchase Order", code: "PO-2481-A", icon: ClipboardCheck },
  { slug: "commercial-invoice", label: "Commercial Invoice", code: "CI-44821", icon: ReceiptText },
  { slug: "packing-list", label: "Packing List", code: "PL-44821", icon: Package },
  { slug: "bill-of-lading", label: "Bill of Lading", code: "BL-MSCU-2481", icon: Anchor },
  { slug: "inspection-report", label: "Inspection Report", code: "INS-7740", icon: FileSignature },
  { slug: "delivery", label: "Delivery", code: "DLV-2481", icon: Truck },
];

export default function ConnectedDocuments() {
  return (
    <section data-testid={DOC_CENTER.connected.section} className="relative bg-[color:var(--dc-bg)] py-28 md:py-36">
      <div className="dc-container">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="dc-eyebrow">Connected documents</span>
            <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-tighter text-ink-950 sm:text-5xl md:text-[56px]">
              Everything connected.<br />Nothing duplicated.
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-ink-600">
              Documents reference each other automatically. The Purchase Order knows its invoice. The
              invoice knows its packing list. The packing list knows its BoL. The operation flows.
            </p>

            <ul className="mt-8 space-y-3 text-[14px] text-ink-700">
              <li className="flex items-start gap-2"><span className="mt-2 h-1 w-1 rounded-full bg-ink-950" /> One shipment, one verified chain of evidence.</li>
              <li className="flex items-start gap-2"><span className="mt-2 h-1 w-1 rounded-full bg-ink-950" /> Any document can be opened from the operation it belongs to.</li>
              <li className="flex items-start gap-2"><span className="mt-2 h-1 w-1 rounded-full bg-ink-950" /> No duplicates. No version drift. No “which one is the latest?”</li>
            </ul>
          </div>

          {/* Flow column */}
          <div className="lg:col-span-7">
            <div className="relative">
              <div className="flex flex-col items-stretch gap-3">
                {NODES.map((n, i) => {
                  const Icon = n.icon;
                  const isFirst = i === 0;
                  const isLast = i === NODES.length - 1;
                  return (
                    <div key={n.slug} className="relative">
                      <div
                        data-testid={DOC_CENTER.connected.node(n.slug)}
                        className={`dc-card dc-card-hover flex items-center justify-between gap-4 px-5 py-4 ${
                          isFirst ? "ring-1 ring-[color:var(--dc-accent)]/30" : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`grid h-10 w-10 place-items-center rounded-lg border ${
                              isFirst
                                ? "border-[color:var(--dc-accent)]/40 bg-[color:var(--dc-accent-bg)] text-[color:var(--dc-accent)]"
                                : isLast
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-[color:var(--dc-border)] bg-white text-ink-900"
                            }`}
                          >
                            <Icon className="h-5 w-5" strokeWidth={1.5} />
                          </div>
                          <div>
                            <div className="text-[14px] font-semibold text-ink-950">{n.label}</div>
                            <div className="mt-0.5 font-mono text-[10.5px] uppercase tracking-wider text-ink-500">{n.code}</div>
                          </div>
                        </div>
                        <span className="hidden font-mono text-[10.5px] uppercase tracking-wider text-ink-400 sm:inline">
                          step 0{i + 1}
                        </span>
                      </div>
                      {!isLast && (
                        <div className="flex items-center justify-center py-1">
                          <div className="relative h-7 w-px bg-[color:var(--dc-border-strong)]">
                            <span className="absolute inset-x-[-3px] top-0 h-full">
                              <span className="absolute inset-0 dc-shimmer rounded-full" />
                            </span>
                          </div>
                          <ArrowDown className="absolute h-3.5 w-3.5 translate-y-[10px] text-ink-400" strokeWidth={1.75} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
